import { createReducer } from '@reduxjs/toolkit'

import type { TCompanyGroupDto } from '@homepal/api-contract'

import { insertItems } from 'redux/utils'

import * as actions from '../../actions/CompanyGroups/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import * as companyActions from '../../actions/Companies/actions'

export interface CompanyStructureSyncStatus {
  // The structure changed after the latest ontology run — data is
  // out of date until the ontology job is rerun.
  stale: boolean
  running: boolean
  starting: boolean
  // A rerun was started from this session — keeps the in-progress
  // state visible until polling sees the run finish.
  triggered: boolean
  fetched: boolean
  ontologyDataProductId: string | null
  expectedDurationMinutes: number | null
}

export interface CompanyGroupReducerType {
  data: { [companyGroupId: string]: TCompanyGroupDto }
  fetched: boolean
  fetching: boolean
  syncStatus: CompanyStructureSyncStatus
}

const INITIAL_STATE: CompanyGroupReducerType = {
  data: {},
  fetched: false,
  fetching: false,
  syncStatus: {
    stale: false,
    running: false,
    starting: false,
    triggered: false,
    fetched: false,
    ontologyDataProductId: null,
    expectedDurationMinutes: null
  }
}

export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getAll, (state) => {
      state.fetching = true
    })
    .addCase(actions.getAllSuccess, (state, action) => {
      state.data = insertItems(action.payload.data)
      state.fetched = true
      state.fetching = false
    })
    .addCase(actions.getAllFailed, (state) => {
      state.fetching = false
    })
    .addCase(actions.createSuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
      state.syncStatus.stale = true
    })
    .addCase(actions.updateSuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
      state.syncStatus.stale = true
    })
    .addCase(actions.destroySuccess, (state, action) => {
      delete state.data[action.payload.id]
      state.syncStatus.stale = true
    })
    .addCase(actions.addCompanySuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
      state.syncStatus.stale = true
    })
    .addCase(actions.removeCompanySuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
      state.syncStatus.stale = true
    })
    // (De)activating a company is also a structure change.
    .addCase(companyActions.updateSuccess, (state) => {
      state.syncStatus.stale = true
    })
    .addCase(actions.getSyncStatusSuccess, (state, action) => {
      state.syncStatus.stale = action.payload.data.stale
      state.syncStatus.running = action.payload.data.ontology_running
      state.syncStatus.ontologyDataProductId =
        action.payload.data.ontology_data_product_id
      state.syncStatus.expectedDurationMinutes =
        action.payload.data.expected_duration_minutes
      state.syncStatus.fetched = true

      if (!action.payload.data.ontology_running) {
        state.syncStatus.triggered = false
      }
    })
    .addCase(actions.getSyncStatusFailed, (state) => {
      state.syncStatus.fetched = true
    })
    .addCase(actions.rerunOntology, (state) => {
      state.syncStatus.starting = true
    })
    .addCase(actions.rerunOntologySuccess, (state) => {
      state.syncStatus.starting = false
      state.syncStatus.stale = false
      state.syncStatus.running = true
      state.syncStatus.triggered = true
    })
    .addCase(actions.rerunOntologyFailed, (state) => {
      state.syncStatus.starting = false
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
})
